import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    input,
    signal,
    viewChild,
} from '@angular/core';
import { TextInput } from '../text-input/text-input.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Icon } from '../../icon/icon.component';

/**
 * Valor fictício do input "decoy" da máscara. O conteúdo é irrelevante (o
 * navegador o mascara com o mesmo glifo que usa numa senha real); o comprimento
 * apenas dá a aparência de "há um valor preenchido".
 */
const MASK_VALUE = 'xxxxxxxxxxxx';

/**
 * Campo de senha, derivado de {@link TextInput}.
 *
 * Adiciona um botão de ícone para alternar a visibilidade do valor (texto vs.
 * senha). Também requer um `NgControl` no host (use com `formControl`/`ngModel`).
 */
@Component({
    selector: 'ai-password',
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [FormsModule, ReactiveFormsModule, Icon],
    templateUrl: 'password.component.html',
    styleUrls: ['password.component.scss'],
})
export class Password extends TextInput {
    /** Conjunto de caracteres permitidos (reservado para validação futura). */
    allowedCharacters = input<string>('');

    /**
     * Exibe uma **máscara** (como um valor de senha preenchido) quando o campo
     * está vazio e sem foco. Útil na **edição** para sinalizar que já existia um
     * valor que não foi trazido por segurança (ex.: credenciais).
     *
     * A máscara é um input "decoy" real (`type=password` com um valor fictício),
     * então o navegador a desenha com **o mesmo glifo do mascaramento nativo**
     * (pontos/asteriscos conforme o browser/SO) — igual a um campo preenchido. Ao
     * clicar/focar, o campo real (vazio) aparece para digitação; ao sair do foco
     * sem digitar, a máscara volta. O **valor do formulário permanece vazio**
     * enquanto o usuário não digita.
     */
    readonly showMaskWhenEmpty = input<boolean>(false);

    /** Indica se a senha está visível (texto) ou oculta. */
    isPasswordVisible = signal<boolean>(false);

    /** `true` enquanto o campo real está com foco. */
    protected readonly focused = signal<boolean>(false);

    /** Valor fictício exibido (mascarado) pelo input decoy. */
    protected readonly maskValue = MASK_VALUE;

    /** Referência ao input real, para receber o foco ao clicar na máscara. */
    private readonly realInput =
        viewChild<ElementRef<HTMLInputElement>>('realInput');

    /**
     * `true` quando a máscara deve ser exibida: modo habilitado, sem foco e
     * campo vazio. Método (reavaliado a cada detecção de mudanças) para
     * acompanhar o valor do controle, que não é um signal.
     */
    protected isMasked(): boolean {
        const value = this.ngControl.control?.value;
        const isEmpty = value === null || value === undefined || value === '';
        return this.showMaskWhenEmpty() && !this.focused() && isEmpty;
    }

    /** Alterna a visibilidade da senha. */
    togglePasswordVisibility(): void {
        this.isPasswordVisible.set(!this.isPasswordVisible());
    }

    /**
     * Ativa o campo real ao clicar na máscara: evita que o decoy roube o foco e
     * transfere o foco para o input real (vazio), pronto para digitação.
     */
    protected activateInput(event: MouseEvent): void {
        event.preventDefault();
        this.focused.set(true);
        this.realInput()?.nativeElement.focus();
    }
}
